import cx from 'classnames';
import { first, map, some } from 'lodash';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import { VtbProbeStatus } from 'components/VtbProbeStatus';
import {
  DEFAULT_SQ_DEEPLINK_STYLE,
  SQ_DEEPLINKS_BANK_STYLES,
} from 'constants/bank-styles';
import { P2P_ORDER_DEEPLINK_ID } from 'constants/deeplink.constants';
import { IMAGES_PATH } from 'constants/images.constants';
import { ROUTE_PATH } from 'constants/routes';
import { Bank, EventName } from 'enums';
import { splitDeeplinkUrls, useDeepLink } from 'hooks/use-deeplink.hook';
import { useOrderContext } from 'hooks/use-order-context.hook';
import { useVtbTlsProbe } from 'hooks/use-vtb-tls-probe.hook';
import { TranslationNamespace } from 'i18n';
import { Deeplink } from 'types';
import { deeplinkUtils, eventUtils, userDeviceUtils } from 'utils';

import { SqLogoImage } from '../SqLogoImage';
import { SqQrCode } from '../SqQrCode';

type Props = {
  deeplinks: Deeplink[];
};

export const SqDeeplinks: React.FC<Props> = ({ deeplinks }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });
  const { processDeeplinkUrl } = useDeepLink();
  const { id, token } = useParams<{ id: string; token: string }>();

  const deviceOS = useMemo(() => userDeviceUtils.getDeviceOS(), []);

  const { showButtons, showQr } = useMemo(
    () => deeplinkUtils.getSqDeeplinkDisplayMode(deviceOS),
    [deviceOS],
  );

  const firstDeeplink = useMemo(() => first(deeplinks), [deeplinks]);
  const bank = firstDeeplink?.customerBank;
  const isP2pPaymentLink = firstDeeplink?.id === P2P_ORDER_DEEPLINK_ID;

  const hasVtbDeeplink = useMemo(
    () => some(deeplinks, (d) => d.customerBank === Bank.VTB),
    [deeplinks],
  );
  const vtbFallbackText = useMemo(() => t('deeplink.vtb_fallback'), [t]);
  const { isReachable, isLoading: isVtbLoading } =
    useVtbTlsProbe(hasVtbDeeplink);

  // encoding a scheme would pin the QR to one OS and overflow the QR size limit,
  // so it carries the page that repeats this detection on whichever phone scans it
  const deeplinkPageUrl = useMemo(() => {
    const path = ROUTE_PATH.DEEPLINK.replace(':id', id ?? '').replace(
      ':token',
      token ?? '',
    );
    const query = bank ? `?customerBank=${encodeURIComponent(bank)}` : '';
    return `${window.location.origin}${path}${query}`;
  }, [id, token, bank]);

  // a p2p provider payment page is a plain URL that any scanner can open,
  // so it goes into the QR as is instead of through the handoff page
  const qrData = useMemo(() => {
    if (!isP2pPaymentLink) {
      return deeplinkPageUrl;
    }
    const processedUrl = firstDeeplink?.url
      ? processDeeplinkUrl(firstDeeplink.url)
      : '';
    return splitDeeplinkUrls(processedUrl)[0]?.trim();
  }, [
    isP2pPaymentLink,
    deeplinkPageUrl,
    firstDeeplink?.url,
    processDeeplinkUrl,
  ]);

  return (
    <div className="tw-my-4">
      {showQr && (
        <div className={cx({ 'tw-mb-4': showButtons })}>
          {bank === Bank.VTB && (isVtbLoading || !isReachable) ? (
            <VtbProbeStatus
              isLoading={isVtbLoading}
              fallbackText={vtbFallbackText}
            />
          ) : (
            <SqQrCode data={qrData} bank={bank as Bank} />
          )}
        </div>
      )}
      {showButtons &&
        map(deeplinks, (deeplink) => {
          if (!deeplink.url) {
            return;
          }
          if (
            deeplink.customerBank === Bank.VTB &&
            (isVtbLoading || !isReachable)
          ) {
            return (
              <VtbProbeStatus
                key={deeplink.id}
                isLoading={isVtbLoading}
                fallbackText={vtbFallbackText}
              />
            );
          }
          return (
            <div key={deeplink.id}>
              <DeepLinkButton
                href={processDeeplinkUrl(deeplink.url)}
                bank={deeplink.customerBank as Bank}
              />
            </div>
          );
        })}
    </div>
  );
};

function DeepLinkButton({ href, bank }: { href: string; bank: Bank }) {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.payment_details',
  });
  const {
    getBankNameByCode,
    paymentTypeFlags: { isDeeplink },
  } = useOrderContext();
  const { openDeepLinks } = useDeepLink();

  const [isLoading, setIsLoading] = useState(false);
  const [isClicked, setIsClicked] = useState(false);

  const style = useMemo(() => SQ_DEEPLINKS_BANK_STYLES[bank], [bank]);

  const title = useMemo(
    () =>
      t('deeplink.title', {
        bankName: getBankNameByCode?.(bank) || bank,
      }),
    [bank, getBankNameByCode, t],
  );

  const content = useMemo(
    () =>
      style?.iconPath ? (
        <Fragment>
          <img
            src={`${IMAGES_PATH}${style.iconPath}`}
            alt={title}
            width="25px"
            height="25px"
          />
          <span
            style={{ color: style?.textColor || '#fff' }}
            className="tw-pl-3"
          >
            {t('deeplink.deposit')}
          </span>
        </Fragment>
      ) : (
        <span>{title}</span>
      ),
    [style, title, t],
  );

  const onClick = useCallback(
    async (e: React.MouseEvent) => {
      e.preventDefault();
      if (isLoading || isClicked) {
        return;
      }
      eventUtils.trackEvent({
        event: EventName.DeeplinkOpen,
      });
      setIsClicked(true);
      setIsLoading(true);
      await openDeepLinks(href);
      setIsLoading(false);
    },
    [href, openDeepLinks, isLoading, isClicked],
  );

  const buttonClassName = cx(
    `tw-w-full tw-h-[54px] tw-rounded-full tw-font-medium tw-text-[17px] tw-flex tw-items-center tw-justify-center`,
    { 'tw-opacity-50 tw-cursor-wait': isLoading },
    { 'tw-opacity-50 tw-cursor-not-allowed tw-pointer-events-none': isClicked },
  );

  const browserHref = splitDeeplinkUrls(href)[0].trim();

  return (
    <>
      <div
        className={buttonClassName}
        style={
          style
            ? { background: style.background }
            : {
                background: DEFAULT_SQ_DEEPLINK_STYLE.background,
                border: DEFAULT_SQ_DEEPLINK_STYLE.border,
              }
        }
        role="button"
        onClick={onClick}
      >
        {content}
      </div>
      {isClicked && !isDeeplink && (
        <a
          href={browserHref}
          target="_blank"
          rel="noopener noreferrer"
          className="tw-w-full tw-h-[54px] tw-mt-4 tw-rounded-full tw-border-2 tw-border-gray-400 tw-bg-white tw-text-gray-700 tw-font-medium tw-text-[17px] tw-flex tw-items-center tw-justify-center tw-gap-2"
        >
          <SqLogoImage
            bank={bank}
            bankName={title}
            className="tw-w-6 tw-h-6 tw-shrink-0"
          />
          <span>{t('deeplink.open_in_browser')}</span>
        </a>
      )}
    </>
  );
}
